import api from 'api';
import { get } from 'utils/fetch';
import { useState, useEffect, useContext } from 'react';
import { message, Spin, Input, Select, InputNumber } from 'antd';
import { PropertyInsertion } from 'pages/RecordInsertion/PropertyInsertion';
import { isPhoneSize } from 'utils/util';
import { GlobalContext } from 'GlobalContext';
import { isAdmin, isSale, propertyToAddress } from 'utils/util';
import { USDollar, parsePgArray } from 'utils/format';
import { enumOptions } from 'pages/AssetManager/AssetManagerContext';

export function BasicInfo() {
    const { manageAssetId, setManageAssetId, accessToken, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(manageAssetId < 0 ? false : true);
    const [propertyBasicInfo, setPropertyBasicInfo] = useState(null);
    const [editMode, setEditMode] = useState(
        manageAssetId === -1 ? true : false
    );
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [parentPropertyId, setParentPropertyId] = useState(null);
    const [titleHolder, setTitleHolder] = useState('');
    const [projectStrategy, setProjectStrategy] = useState([]);
    const [businessLine, setBusinessLine] = useState('');
    const [iFund, setIFund] = useState('');
    const [propertyType, setPropertyType] = useState('');
    const [occupancyStatus, setOccupancyStatus] = useState('');
    const [hoa, setHoa] = useState(false);
    const [solarPanel, setSolarPanel] = useState(false);
    const [insurancePolicy, setInsurancePolicy] = useState([]);
    const [annualPropertyTax, setAnnualPropertyTax] = useState(0);
    const [annualInsurancePremium, setAnnualInsurancePremium] = useState(0);
    const [asIsMarketValue, setAsIsMarketValue] = useState(0);
    const [equity, setEquity] = useState(0);
    const [monthlyPiti, setMonthlyPiti] = useState(0);
    const [ltv, setLtv] = useState(0);
    const [grandDeed, setGrandDeed] = useState([]);
    const [properties, setProperties] = useState([]);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [newAddress, setNewAddress] = useState(null);
    const [exitPipeline, setExitPipeline] = useState(null);
    const [exitPipelineYear, setExitPipelineYear] = useState(null);
    const [exitPipelineQuarter, setExitPipelineQuarter] = useState(null);
    const [pipelineCategory, setPipelineCategory] = useState(null);
    const [investor, setInvestor] = useState(null);

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchPropertyBasicInfo(id) {
        await get(
            api.asset.propertyBasicInfo(id),
            setPropertyBasicInfo,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    function restore() {
        setAddress(propertyBasicInfo?.address ?? '');
        setCity(propertyBasicInfo?.city ?? '');
        setCounty(propertyBasicInfo?.county ?? '');
        setState(propertyBasicInfo?.state ?? '');
        setZip(propertyBasicInfo?.zip ?? '');
        setParentPropertyId(propertyBasicInfo?.parent_id);
        setTitleHolder(propertyBasicInfo?.titleholder ?? '');
        setProjectStrategy(
            parsePgArray(propertyBasicInfo?.project_strategy ?? [])
        );
        setBusinessLine(parsePgArray(propertyBasicInfo?.business_line ?? ''));
        setIFund(propertyBasicInfo?.fund ?? '');
        setPropertyType(parsePgArray(propertyBasicInfo?.property_type ?? ''));
        setOccupancyStatus(
            parsePgArray(propertyBasicInfo?.occupancy_status ?? '')
        );
        setHoa(propertyBasicInfo?.hoa_status ?? false);
        setSolarPanel(propertyBasicInfo?.solar_panel ?? false);
        setInsurancePolicy(propertyBasicInfo?.insurance_policy ?? []);
        setAnnualPropertyTax(propertyBasicInfo?.annual_property_tax ?? 0);
        setAnnualInsurancePremium(
            propertyBasicInfo?.annual_insurance_premium ?? 0
        );
        setAsIsMarketValue(propertyBasicInfo?.as_is_value ?? 0);
        setEquity(propertyBasicInfo?.equity ?? 0);
        setMonthlyPiti(propertyBasicInfo?.monthly_piti ?? 0);
        setLtv(propertyBasicInfo?.ltv ?? 0);
        setGrandDeed(propertyBasicInfo?.grand_deed_prelim ?? []);
        setLatitude(propertyBasicInfo?.latitude);
        setLongitude(propertyBasicInfo?.longitude);
        setNewAddress(propertyBasicInfo?.new_address);
        if (
            propertyBasicInfo?.exit_pipeline &&
            propertyBasicInfo?.exit_pipeline.includes('-')
        ) {
            const [year, querter] = propertyBasicInfo.exit_pipeline.split('-');
            setExitPipeline(propertyBasicInfo?.exit_pipeline);
            setExitPipelineYear(year);
            setExitPipelineQuarter(querter);
        }
        setPipelineCategory(parsePgArray(propertyBasicInfo?.pipeline_category));
        setInvestor(parsePgArray(propertyBasicInfo?.investor));
    }

    async function fetchProperties() {
        await get(
            api.manager.properties,
            setProperties,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
    }

    useEffect(() => {
        if (propertyBasicInfo) {
            restore();
        }
    }, [propertyBasicInfo]);

    useEffect(() => {
        if (manageAssetId < 0) {
            return;
        }
        fetchPropertyBasicInfo(manageAssetId);
        fetchProperties();
    }, [manageAssetId]);

    function recordChanged() {
        return true;
    }

    async function savePropertyBasicInfo() {
        if (!recordChanged()) {
            return;
        }
        setLoading(true);
        setAddress(address.trim());
        setCity(city.trim());
        setCounty(county.trim());
        setState(state.trim());
        setZip(zip.trim());
        const fullAddress = `${address}, ${city}, ${state} ${zip}`;

        var newLat = 37.32550095925802;
        var newLong = -121.92818162476449;

        if (!longitude || !latitude) {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
                    fullAddress
                )}`
            );
            const data = await response.json();
            if (data.length > 0) {
                newLat = parseFloat(data[0].lat);
                newLong = parseFloat(data[0].lon);
            }
        }

        const body = JSON.stringify({
            address,
            city,
            county,
            state,
            zip,
            parentPropertyId: parentPropertyId ?? null,
            fullAddress,
            latitude: latitude && longitude ? latitude : newLat,
            longitude: latitude && longitude ? longitude : newLong,
            titleHolder,
            projectStrategy,
            businessLine,
            iFund,
            propertyType,
            occupancyStatus,
            hoa,
            solarPanel,
            insurancePolicy,
            annualPropertyTax,
            annualInsurancePremium,
            asIsMarketValue,
            equity,
            monthlyPiti,
            ltv,
            grandDeed,
            newAddress,
            exitPipeline:
                exitPipelineYear && exitPipelineQuarter
                    ? `${exitPipelineYear}-${exitPipelineQuarter}`
                    : null,
            pipelineCategory,
            investor,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        var retId = 0;
        if (manageAssetId < 0) {
            const response = await fetch(
                api.asset.insertPropertyBasicInfo,
                options
            );
            retId = await response.json();
        } else {
            await fetch(
                api.asset.updatePropertyBasicInfo(manageAssetId),
                options
            );
            retId = manageAssetId;
        }
        if (retId === 0) {
            setLoading(false);
            setManageAssetId(0);
            return;
        }
        setManageAssetId(retId);
        fetchPropertyBasicInfo(retId);
        setEditMode(false);
    }

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <>
                    {manageAssetId > 0 && (
                        <div
                            style={{
                                width: 'fit-content',
                                marginLeft: '20px',
                                height: '38px',
                                position: 'fixed',
                                background: 'white',
                                width: '100%',
                                alignContent: 'end',
                            }}
                        >
                            <div
                                style={{
                                    color: 'blue',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    width: 'fit-content',
                                }}
                                onClick={(e) => {
                                    if (editMode) {
                                        restore();
                                    }
                                    setEditMode(!editMode);
                                }}
                            >
                                {editMode ? 'Restore' : 'Edit'}
                            </div>
                        </div>
                    )}
                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : {
                                      ...entryStyle,
                                      display: 'flex',
                                      marginTop: '50px',
                                  }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Address: </div>
                                <input
                                    style={inputStyle}
                                    value={address}
                                    onInput={(e) => {
                                        setAddress(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `Address: ${address}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>City: </div>
                                <input
                                    style={inputStyle}
                                    value={city}
                                    onInput={(e) => {
                                        setCity(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `City: ${city}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>County: </div>
                                <input
                                    style={inputStyle}
                                    value={county}
                                    onInput={(e) => {
                                        setCounty(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `County: ${county}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>State: </div>
                                <input
                                    style={inputStyle}
                                    value={state}
                                    onInput={(e) => {
                                        setState(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `State: ${state}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Zip: </div>
                                <input
                                    style={inputStyle}
                                    value={zip}
                                    onInput={(e) => {
                                        setZip(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `Zip: ${zip}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>New Address: </div>
                                <input
                                    style={inputStyle}
                                    value={newAddress}
                                    onInput={(e) => {
                                        setNewAddress(e.target.value);
                                    }}
                                ></input>
                            </>
                        )}

                        {!editMode && `New Address: ${newAddress || ''}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Parent Property: </div>
                                <Select
                                    showSearch
                                    allowClear
                                    optionFilterProp="label"
                                    value={parentPropertyId}
                                    onChange={(e) => {
                                        setParentPropertyId(e);
                                    }}
                                    placeholder="Choose property"
                                    style={{
                                        width: '150px',
                                        background: 'transparent',
                                    }}
                                    filterOption={(input, option) =>
                                        option?.label
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    // optionFilterProp="children"
                                    options={properties
                                        .filter(
                                            (property) =>
                                                property.id !== manageAssetId
                                        )
                                        .sort((a, b) => {
                                            if (
                                                propertyToAddress(
                                                    a
                                                ).toLowerCase() <
                                                propertyToAddress(
                                                    b
                                                ).toLowerCase()
                                            ) {
                                                return -1;
                                            }
                                            return 1;
                                        })
                                        .map((property) => {
                                            return {
                                                value: property.id,
                                                label: `${propertyToAddress(
                                                    property
                                                )}`,
                                            };
                                        })}
                                ></Select>
                            </>
                        )}

                        {!editMode &&
                            `Parent Property: ${
                                propertyToAddress(
                                    properties.find(
                                        (p) => p.id === parentPropertyId
                                    )
                                ) ?? 'N/A'
                            }`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Exit Pipeline: </div>
                                <InputNumber
                                    min={2000}
                                    max={2100}
                                    placeholder="year (yyyy)"
                                    value={exitPipelineYear}
                                    onChange={setExitPipelineYear}
                                    style={{ width: 73, height: 24 }}
                                />
                                -
                                <Select
                                    value={exitPipelineQuarter}
                                    placeholder="querter"
                                    onChange={setExitPipelineQuarter}
                                    style={{ width: 73 }}
                                    options={[
                                        { value: 'Q1', label: 'Q1' },
                                        { value: 'Q2', label: 'Q2' },
                                        { value: 'Q3', label: 'Q3' },
                                        { value: 'Q4', label: 'Q4' },
                                    ]}
                                />
                            </>
                        )}

                        {!editMode && `Exit Pipeline: ${exitPipeline || ''}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Pipeline Category: </div>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select pipeline categories"
                                    style={{ width: '300px' }}
                                    value={pipelineCategory}
                                    onChange={setPipelineCategory}
                                    options={enumOptions['Pipeline Category']
                                        .sort((a, b) => {
                                            return a < b ? -1 : 1;
                                        })
                                        .map((opt) => ({
                                            label: opt,
                                            value: opt,
                                        }))}
                                    maxTagCount="responsive"
                                />
                            </>
                        )}

                        {!editMode &&
                            `Pipeline Category: ${
                                pipelineCategory
                                    ? pipelineCategory.join(', ')
                                    : ''
                            }`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode && (
                            <>
                                <div>Investor: </div>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select investors"
                                    style={{ width: '300px' }}
                                    value={investor}
                                    onChange={setInvestor}
                                    options={enumOptions['Investor']
                                        .sort((a, b) => {
                                            return a < b ? -1 : 1;
                                        })
                                        .map((opt) => ({
                                            label: opt,
                                            value: opt,
                                        }))}
                                    maxTagCount="responsive"
                                />
                            </>
                        )}

                        {!editMode &&
                            `Investor: ${investor ? investor.join(', ') : ''}`}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Title Holder:</div>
                                <input
                                    style={inputStyle}
                                    value={titleHolder}
                                    onInput={(e) =>
                                        setTitleHolder(e.target.value)
                                    }
                                />
                            </>
                        ) : (
                            `Title Holder: ${titleHolder}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Project Strategy:</div>
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Select project strategy: "
                                    value={projectStrategy}
                                    style={{ width: 200 }}
                                    onChange={(selected) =>
                                        setProjectStrategy(selected)
                                    }
                                    options={enumOptions[
                                        'Project Strategy'
                                    ].map((status) => ({
                                        value: status,
                                        label: status,
                                    }))}
                                />
                            </>
                        ) : (
                            `Project Strategy: ${projectStrategy}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Business Line:</div>
                                <Select
                                    // mode="multiple"
                                    showSearch
                                    placeholder="Select Business lines: "
                                    value={businessLine || null}
                                    style={{ width: 200 }}
                                    onChange={(selected) =>
                                        setBusinessLine(selected)
                                    }
                                    options={enumOptions['Business Line'].map(
                                        (status) => ({
                                            value: status,
                                            label: status,
                                        })
                                    )}
                                />
                            </>
                        ) : (
                            `Business Line: ${businessLine}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>I-Fund:</div>
                                <input
                                    style={inputStyle}
                                    value={iFund}
                                    onInput={(e) => setIFund(e.target.value)}
                                />
                            </>
                        ) : (
                            `I-Fund: ${iFund}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Property Type:</div>
                                <Select
                                    // mode="multiple"
                                    showSearch
                                    placeholder="Select Property Type"
                                    value={propertyType || null}
                                    style={{ width: 200 }}
                                    onChange={(selected) =>
                                        setPropertyType(selected)
                                    }
                                    options={enumOptions['Property Type'].map(
                                        (status) => ({
                                            value: status,
                                            label: status,
                                        })
                                    )}
                                />
                            </>
                        ) : (
                            `Property Type: ${propertyType}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Occupancy Status:</div>
                                <Select
                                    // mode="multiple"
                                    showSearch
                                    placeholder="Select Occupancy Status"
                                    value={occupancyStatus || null}
                                    style={{ width: 200 }}
                                    onChange={(selected) =>
                                        setOccupancyStatus(selected)
                                    }
                                    options={enumOptions[
                                        'Occupancy Status'
                                    ].map((status) => ({
                                        value: status,
                                        label: status,
                                    }))}
                                />
                            </>
                        ) : (
                            `Occupancy Status: ${occupancyStatus}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <label>
                                <input
                                    type="checkbox"
                                    checked={hoa}
                                    onChange={(e) => setHoa(e.target.checked)}
                                />{' '}
                                HOA
                            </label>
                        ) : (
                            `HOA: ${hoa ? 'Yes' : 'No'}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <label>
                                <input
                                    type="checkbox"
                                    checked={solarPanel}
                                    onChange={(e) =>
                                        setSolarPanel(e.target.checked)
                                    }
                                />{' '}
                                Solar Panel
                            </label>
                        ) : (
                            `Solar Panel: ${solarPanel ? 'Yes' : 'No'}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Insurance Policy (comma-separated):</div>
                                <input
                                    style={inputStyle}
                                    value={insurancePolicy.join(', ')}
                                    onInput={(e) =>
                                        setInsurancePolicy(
                                            e.target.value
                                                .split(',')
                                                .map((str) => str.trim())
                                        )
                                    }
                                />
                            </>
                        ) : (
                            `Insurance Policy: ${insurancePolicy.join(', ')}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        {editMode ? (
                            <>
                                <div>Annual Property Tax:</div>
                                <input
                                    style={inputStyle}
                                    type="number"
                                    value={annualPropertyTax}
                                    onInput={(e) =>
                                        setAnnualPropertyTax(e.target.value)
                                    }
                                />
                            </>
                        ) : (
                            `Annual Property Tax: ${USDollar.format(
                                annualPropertyTax
                            )}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>Annual Insurance Premium:</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                type="number"
                                value={annualInsurancePremium}
                                onInput={(e) =>
                                    setAnnualInsurancePremium(e.target.value)
                                }
                            />
                        ) : (
                            `${USDollar.format(annualInsurancePremium)}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>As-is Market Value:</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                type="number"
                                value={asIsMarketValue}
                                onInput={(e) =>
                                    setAsIsMarketValue(e.target.value)
                                }
                            />
                        ) : (
                            `${USDollar.format(asIsMarketValue)}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>Equity:</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                type="number"
                                value={equity}
                                onInput={(e) => setEquity(e.target.value)}
                            />
                        ) : (
                            `${USDollar.format(equity)}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>Monthly PITI:</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                type="number"
                                value={monthlyPiti}
                                onInput={(e) => setMonthlyPiti(e.target.value)}
                            />
                        ) : (
                            `${USDollar.format(monthlyPiti)}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>LTV (%):</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                type="number"
                                value={ltv}
                                onInput={(e) => setLtv(e.target.value)}
                            />
                        ) : (
                            `${ltv}`
                        )}
                    </div>

                    <div
                        style={
                            isPhoneSize()
                                ? entryStyle
                                : { ...entryStyle, display: 'flex' }
                        }
                    >
                        <div>Grand Deed:</div>
                        {editMode ? (
                            <input
                                style={inputStyle}
                                value={grandDeed.join(', ')}
                                onInput={(e) =>
                                    setGrandDeed(
                                        e.target.value
                                            .split(',')
                                            .map((str) => str.trim())
                                    )
                                }
                            />
                        ) : (
                            <span>
                                {grandDeed.map((link, index) => (
                                    <span key={index}>
                                        <a
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {link}
                                        </a>
                                        {index < grandDeed.length - 1
                                            ? ', '
                                            : ''}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>

                    <div
                        style={{
                            margin: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <button
                            style={{
                                height: '30px',
                                width: '120px',
                                borderRadius: '30px',
                                background: 'rgb(17 32 86)',
                                color: 'white',
                                cursor: 'pointer',
                            }}
                            onClick={savePropertyBasicInfo}
                        >
                            Save Basic Info
                        </button>
                    </div>
                </>
            )}
        </>
    );
}
